/**
 * Here are helpers. Variables and function that are needed in multiple js files.
 */

import $ from 'jquery';

// Variables
export const BOOTSTRAP_BREAKPOINT_SMALL = 768;
export const BOOTSTRAP_BREAKPOINT_MEDIUM = 992;
export const BOOTSTRAP_BREAKPOINT_LARGE = 1200;
export const BOOTSTRAP_BREAKPOINT_EXTRA_LARGE = 1400;
export const BREAKPOINT_HEIGHT_SMALL = 600;
export const BREAKPOINT_HEIGHT_MEDIUM = 760;
export const BREAKPOINT_HEIGHT_LARGE = 850;
export const BREAKPOINT_HEIGHT_EXTRA_LARGE = 1100;
export const CONTAINER_MAX_WIDTH = 1660;
export const CLASS_SCROLL_DISABLED = 'scroll-disabled';
export const CLASS_TOUCH_SCREEN = 'touch-screen';
export const CLASS_ACTIVE = 'active';
export const CLASS_BOOTSTRAP_DISPLAY_NONE = 'd-none';
export const CLASS_BOOTSTRAP_OVERFLOW_HIDDEN = 'overflow-hidden';
export const CLASS_BOOTSTRAP_CENTER_CONTENT = 'center-content';
export const LOCAL_STORAGE_KEY_SMALLEST_HEIGHT = 'SMALLEST_WINDOW_INNER_HEIGHT';

/**
 * Removes preloader element from dom.
 */
export const removePreloader = () => {
  const preloaderElement = document.querySelector('[data-preloader]');
  preloaderElement.remove();
}

/**
 * Checks if current view size is dekstop.
 * 
 * @returns {Boolean} Returns true if view size is desktop.
 */
export const isDesktop = () => {
  return (window.innerWidth >= BOOTSTRAP_BREAKPOINT_MEDIUM) ? true : false;
}

/**
 * Checks if current view size is mobile.
 * 
 * @returns {Boolean} Returns true if view size is mobile.
 */
export const isMobile = () => {
  return window.innerWidth < BOOTSTRAP_BREAKPOINT_SMALL
}

/**
 * Calculates container position from the side of the browser window.
 * 
 * @returns {number} Pixels from the side of the browser view.
 */
export const calculateContainerPositionFromSide = () => {
  return (window.innerWidth > CONTAINER_MAX_WIDTH) ? (window.innerWidth - CONTAINER_MAX_WIDTH) / 2 : 0;
}

/**
 * Calculates scrollbar width in current browser.
 *
 * @return {Number} Scrollbar width in pixels
 * @see https://stackoverflow.com/a/16771535
 */
export const getScrollbarWidth = () => {
  let div, width = getScrollbarWidth.width;
  if (width === undefined) {
    div = document.createElement('div');
    div.innerHTML = '<div style="width:50px;height:50px;position:absolute;left:-50px;top:-50px;overflow:auto;"><div style="width:1px;height:100px;"></div></div>';
    div = div.firstChild;
    document.body.appendChild(div);
    width = getScrollbarWidth.width = div.offsetWidth - div.clientWidth;
    document.body.removeChild(div);
  }
  return width;
};

/**
 * Check whether current device has touch screen.
 *
 * @return {Boolean} True if touch screen is detected.
 * @see https://stackoverflow.com/a/4819886
 */
export const isTouchScreen = () => {
  const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');

  const mq = function (query) {
    return window.matchMedia(query).matches;
  };

  if (('ontouchstart' in window) || window.DocumentTouch && document instanceof window.DocumentTouch) {
    return true;
  }

  // include the 'heartz' as a way to have a non matching MQ to help terminate the join
  // https://git.io/vznFH
  const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
  return mq(query);
}

/**
 * Gets markup from Stage by given parameters.
 * 
 * @param {Object} params Ajax function parameters
 * @param {Object} params.data Data parameter for ajax function
 * @param {Object} params.onDone done function for ajax call
 */
export const getMarkupFromBackend = (params) => {
  $.ajax({
    data: params.data,
    dataType: 'html',
    method: 'get',
    timeout: 30000,
    url: params.ajaxUrl + '?stage-language=' + document.documentElement.lang
  })
  .done(params.onDone);
}

/**
 * Checks if modal is currently open.
 * 
 * @returns {boolean} True if modal is open.
 */
export const isModalOpen = () => {
  return document.querySelector('body').classList.contains('modal-open');
}

/**
 * Scrolls page to the top.
 */
export const scrollToTop = () => {
  window.scrollTo(0,0);
}

/**
 * Checks if body has no scroll-disabled class
 * 
 * @returns {boolean} Returns true if body has no scroll-disabled class
 */
export const scrollEnabled = () => {
  return !document.querySelector('body').classList.contains(CLASS_SCROLL_DISABLED);
}

/**
 * Checks if body has scroll-disabled class
 * 
 * @returns {boolean} Returns true if body has scroll-disabled class
 */
export const disableScroll = () => {
  return document.querySelector('body').classList.add(CLASS_SCROLL_DISABLED);
}

/**
 * Removes scroll-disabled class from body if it exists
 */
export const enableScroll = () => {
  if (!scrollEnabled()) {
    document.querySelector('body').classList.remove(CLASS_SCROLL_DISABLED);
  }
}

/**
 * Toggles scroll-disabled class on body
 */
export const toggleScrollState = () => {
  if (scrollEnabled()) {
    disableScroll();
  } else {
    enableScroll();
  }
}

/**
 * Sets content for og meta tag.
 * 
 * @param {String} propertyName Og property name
 * @param {String} content Og property content
 */
export const setOgProperty = (propertyName, content) => {
  document.querySelector(`meta[property="og:${propertyName}"]`).setAttribute('content', content);
}

/**
 * Changes meta element's content.
 * 
 * @param {string} name Meta element's name.
 * @param {string} content New content for meta element.
 */
export const setMetaContent = (name, content) => {
  const metaElement = document.querySelector(`meta[name="${name}"]`);

  // Check if meta exists
  if (typeof metaElement !== 'undefined' && metaElement !== null) {
    metaElement.setAttribute('content', content);
  }
}

/**
 * Sets document title.
 * 
 * @param {string} title New title
 */
export const setDocumentTitle = (title) => {
  document.title = title;
}

/**
 * Converts given vh value to pixels based on locked vh height (SAMPOTEST-7)
 *
 * @param {number} vhValue Vh value for example 12vh
 * @returns {string} Pixel value for example 200px
 */
export const convertVhToPixels = (vhValue) => {
  const vhInPixels = localStorage.getItem('vhInPixels');
  return (parseFloat(vhValue.replace('vh','')) * vhInPixels) + 'px';
}

/**
 * Sets document style property for --vh to 1vh (SAMPOTEST-7)
 */
export const resetVhValue = () => {
  document.documentElement.style.setProperty('--vh', '1vh');
}

/**
 * Calculates vh value by given window height (SAMPOTEST-7)
 * 
 * @param {number} windowHeight Current browser window height.
 */
export const setVhValue = (windowHeight) => {
  let vh = parseInt(windowHeight) * 0.01;
  // Then we set the value in the --vh custom property to the root of the document (this is used in css in small desktop breakpoints basically tablet landscape)
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  localStorage.setItem('vhInPixels', vh);
}

/**
 * Parsed userAgent data for checking devices.
 *
 * @see https://stackoverflow.com/a/12606298
 */
export const isDevice = {
  Windows: function() {
    return /IEMobile/i.test(navigator.userAgent);
  },
  Android: function() {
    return /Android/i.test(navigator.userAgent);
  },
  BlackBerry: function() {
    return /BlackBerry/i.test(navigator.userAgent);
  },
  iOS: function() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  },
  any: function() {
    return (isMobile.Android() || isMobile.BlackBerry() || isMobile.iOS() || isMobile.Windows());
  }
};

/**
 * Checks for current browser
 */
export const isBrowser = {
  // IE11 https://stackoverflow.com/a/21825207
  IE11: !!window.MSInputMethodContext && !!document.documentMode,
  // Chrome https://stackoverflow.com/a/4565120
  Chrome: /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor),
  // Safari https://stackoverflow.com/a/23522755
  Safari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
  // Samsung https://stackoverflow.com/a/40684162
  Samsung: navigator.userAgent.match(/SamsungBrowser/i)
}
